import React, { Fragment, useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Container,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { Plus, Edit2, Trash2, RefreshCw } from "react-feather";
import { useSelector } from "react-redux";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";
import ToggleSwitch from "../../shared/ToggleSwitch";
import { errorToast } from "../../shared/Toast";
import {
  getBuzzerSettingsByMerchant,
  createBuzzerSettings,
  updateBuzzerSettings,
  toggleBuzzerSettingsActive,
  deleteBuzzerSettings,
  getBuzzerEvents,
} from "../../utils/apiCalls";

// Provider is the extensibility hook — WAGS MQTT for now, more can be added later.
const BUZZER_PROVIDERS = ["wags"];

const DEFAULT_FORM = {
  provider: "wags",
  name: "",
  brokerHost: "",
  brokerPort: 1883,
  username: "",
  password: "",
};

const primaryBtn = { background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13, color: C.white };
const secondaryBtn = { background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 };
const filterSelectStyle = { padding: "7px 12px", fontSize: 12, borderRadius: 8, border: `1.5px solid ${C.border}`, outline: "none", color: C.text, background: C.white, cursor: "pointer", minWidth: 110 };
const iconBtnStyle = { width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: C.white };
const pagerBtn = (disabled) => ({ padding: "6px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: disabled ? C.bg : C.white, color: disabled ? C.textLight : C.text, fontWeight: 600, fontSize: 12, cursor: disabled ? "default" : "pointer" });

const EVENTS_PAGE_SIZE = 25;

const formatEventTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "-";
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${day} ${month}, ${h}:${m} ${ampm}`;
};

const EventStatusPill = ({ status }) => {
  const ok = status === "sent";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: ok ? C.greenLight : "#FDECEC", color: ok ? C.green : "#dc3545" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: ok ? C.green : "#dc3545" }} />
      {ok ? "Sent" : "Failed"}
    </span>
  );
};

const StatusPill = ({ active }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: active ? C.greenLight : C.borderLight, color: active ? C.green : C.textMed }}>
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? C.green : C.textLight }} />
    {active ? "Active" : "Inactive"}
  </span>
);

const ActionIcon = ({ icon: Icon, color, title, onClick }) => (
  <div onClick={onClick} title={title}
    style={{ width: 32, height: 32, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "all 0.15s", border: `1px solid ${C.border}`, background: C.white }}
    onMouseEnter={e => { e.currentTarget.style.background = C.coralLight; e.currentTarget.style.borderColor = C.coral; }}
    onMouseLeave={e => { e.currentTarget.style.background = C.white; e.currentTarget.style.borderColor = C.border; }}>
    <Icon size={15} color={color} />
  </div>
);

const BuzzerIntegration = () => {
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchantId = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

  const [buzzerSettings, setBuzzerSettings] = useState([]);
  const [isFetching, setIsFetching] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [editingSettings, setEditingSettings] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Recent buzzer logs (last 24h; auto-pruned server-side)
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsPage, setEventsPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");

  const fetchBuzzerSettings = async (merchantId) => {
    if (!merchantId) return;
    setIsFetching(true);
    const data = await getBuzzerSettingsByMerchant(merchantId);
    setBuzzerSettings(data || []);
    setIsFetching(false);
  };

  useEffect(() => {
    if (selectedMerchantId) {
      fetchBuzzerSettings(selectedMerchantId);
    } else {
      setBuzzerSettings([]);
    }
  }, [selectedMerchantId]);

  const fetchEvents = async (merchantId, page, status) => {
    if (!merchantId) return;
    setEventsLoading(true);
    const params = { page, limit: EVENTS_PAGE_SIZE };
    if (status) params.status = status;
    const data = await getBuzzerEvents(merchantId, params);
    setEvents(data?.events || []);
    setEventsTotal(data?.total || 0);
    setEventsLoading(false);
  };

  // Reset to page 1 whenever the merchant or the status filter changes.
  useEffect(() => {
    setEventsPage(1);
  }, [selectedMerchantId, statusFilter]);

  useEffect(() => {
    if (selectedMerchantId) {
      fetchEvents(selectedMerchantId, eventsPage, statusFilter);
    } else {
      setEvents([]);
      setEventsTotal(0);
    }
  }, [selectedMerchantId, eventsPage, statusFilter]);

  const eventsTotalPages = Math.max(1, Math.ceil(eventsTotal / EVENTS_PAGE_SIZE));

  const resetForm = () => {
    setFormData(DEFAULT_FORM);
    setEditingSettings(null);
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (settings) => {
    setEditingSettings(settings);
    setFormData({
      provider: settings.provider || "wags",
      name: settings.name || "",
      brokerHost: settings.brokerHost || "",
      brokerPort: settings.brokerPort || 1883,
      username: settings.username || "",
      password: "",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.provider || !formData.brokerHost) {
      errorToast("Provider and Broker Host are required");
      return;
    }

    if (editingSettings) {
      const payload = { ...formData };
      // "leave blank to keep current" — never send an empty password.
      if (!payload.password) delete payload.password;
      const result = await updateBuzzerSettings(editingSettings.id, payload);
      if (result) {
        setModalOpen(false);
        fetchBuzzerSettings(selectedMerchantId);
      }
    } else {
      const result = await createBuzzerSettings({ ...formData, merchantId: selectedMerchantId });
      if (result) {
        setModalOpen(false);
        fetchBuzzerSettings(selectedMerchantId);
      }
    }
  };

  const handleToggleActive = async (id) => {
    const result = await toggleBuzzerSettingsActive(id);
    if (result) fetchBuzzerSettings(selectedMerchantId);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const result = await deleteBuzzerSettings(deleteTarget.id);
    if (result) {
      setDeleteModal(false);
      setDeleteTarget(null);
      fetchBuzzerSettings(selectedMerchantId);
    }
  };

  return (
    <Fragment>
      <Breadcrumb title="Buzzer Integration" parent="Merchants" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">
          <RCard>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Buzzer / Pager Integrations</div>
              {selectedMerchantId && (
                <button onClick={openCreateModal}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 10, border: "none", background: C.coral, color: C.white, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                  <Plus size={15} /> Add Integration
                </button>
              )}
            </div>

            {!selectedMerchantId ? (
              <div style={{ textAlign: "center", padding: 40, color: C.textLight }}>
                Please select a merchant from the header to view buzzer integrations.
              </div>
            ) : isFetching ? (
              <RLoader />
            ) : (
              <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>ID</th>
                      <th style={tbl.th}>Provider</th>
                      <th style={tbl.th}>Name</th>
                      <th style={tbl.th}>Broker Host</th>
                      <th style={tbl.th}>Port</th>
                      <th style={tbl.th}>Status</th>
                      <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buzzerSettings.length > 0 ? (
                      buzzerSettings.map((settings) => (
                        <tr key={settings.id} className="rpt-row">
                          <td style={{ ...tbl.td, color: C.textLight, fontWeight: 600 }}>{settings.id}</td>
                          <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>{settings.provider}</td>
                          <td style={tbl.td}>{settings.name || "-"}</td>
                          <td style={tbl.td}>{settings.brokerHost}</td>
                          <td style={tbl.td}>{settings.brokerPort}</td>
                          <td style={tbl.td}><StatusPill active={settings.is_active} /></td>
                          <td style={tbl.td}>
                            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 10 }}>
                              <ActionIcon icon={Edit2} color={C.coral} title="Edit" onClick={() => openEditModal(settings)} />
                              <ToggleSwitch checked={settings.is_active} handleToggle={() => handleToggleActive(settings.id)} />
                              <ActionIcon icon={Trash2} color="#dc3545" title="Delete" onClick={() => { setDeleteTarget(settings); setDeleteModal(true); }} />
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>
                          No buzzer integrations configured for this merchant.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>

          <div style={{ marginTop: 20 }}>
            <RCard>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Recent Buzzer Logs</div>
                  <div style={{ fontSize: 11, color: C.textLight, marginTop: 2 }}>Paging events from the last 24 hours (auto-pruned).</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={filterSelectStyle}>
                    <option value="">All</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                  </select>
                  <div onClick={() => fetchEvents(selectedMerchantId, eventsPage, statusFilter)} title="Refresh" style={iconBtnStyle}>
                    <RefreshCw size={15} color={C.textMed} />
                  </div>
                </div>
              </div>

              {!selectedMerchantId ? (
                <div style={{ textAlign: "center", padding: 30, color: C.textLight }}>
                  Select a merchant from the header to view buzzer logs.
                </div>
              ) : eventsLoading ? (
                <RLoader />
              ) : (
                <>
                  <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                    <table style={tbl.table}>
                      <thead>
                        <tr>
                          <th style={tbl.th}>Time</th>
                          <th style={tbl.th}>Buzzer #</th>
                          <th style={tbl.th}>Branch</th>
                          <th style={tbl.th}>Order Ref</th>
                          <th style={tbl.th}>Topic</th>
                          <th style={tbl.th}>Status</th>
                          <th style={tbl.th}>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {events.length > 0 ? (
                          events.map((ev) => (
                            <tr key={ev.id} className="rpt-row">
                              <td style={tbl.td}>{formatEventTime(ev.createdAt)}</td>
                              <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>{ev.buzzerNumber}</td>
                              <td style={tbl.td}>{ev.branchId || "-"}</td>
                              <td style={tbl.td}>{ev.orderReference || "-"}</td>
                              <td style={tbl.td}>{ev.topic || "-"}</td>
                              <td style={tbl.td}><EventStatusPill status={ev.status} /></td>
                              <td style={{ ...tbl.td, color: "#dc3545", maxWidth: 240, whiteSpace: "normal" }}>{ev.error || ""}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" style={{ ...tbl.td, textAlign: "center", padding: 30, color: C.textLight }}>
                              No buzzer events in the last 24 hours.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {eventsTotal > EVENTS_PAGE_SIZE && (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                      <span style={{ fontSize: 12, color: C.textLight }}>{eventsTotal} events</span>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <button disabled={eventsPage <= 1} onClick={() => setEventsPage((p) => Math.max(1, p - 1))} style={pagerBtn(eventsPage <= 1)}>Prev</button>
                        <span style={{ fontSize: 12, color: C.textMed }}>Page {eventsPage} / {eventsTotalPages}</span>
                        <button disabled={eventsPage >= eventsTotalPages} onClick={() => setEventsPage((p) => p + 1)} style={pagerBtn(eventsPage >= eventsTotalPages)}>Next</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </RCard>
          </div>
        </div>
      </Container>

      {/* Create / Edit Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setModalOpen(false)} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700, color: C.text }}>
            {editingSettings ? "Edit Buzzer Integration" : "Add Buzzer Integration"}
          </span>
        </ModalHeader>
        <ModalBody style={{ padding: 24, fontFamily: "'Inter', sans-serif" }}>
          <FormGroup>
            <Label>Provider *</Label>
            <Input type="select" value={formData.provider} onChange={(e) => setFormData({ ...formData, provider: e.target.value })}>
              {BUZZER_PROVIDERS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Name</Label>
            <Input type="text" placeholder="Optional label (e.g. Store pagers)" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Broker Host *</Label>
            <Input type="text" placeholder="e.g. 54.72.162.20" value={formData.brokerHost} onChange={(e) => setFormData({ ...formData, brokerHost: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Broker Port *</Label>
            <Input type="number" value={formData.brokerPort} onChange={(e) => setFormData({ ...formData, brokerPort: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Username</Label>
            <Input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} />
          </FormGroup>
          <FormGroup>
            <Label>Password {editingSettings && "(leave blank to keep current)"}</Label>
            <Input type="password" autoComplete="new-password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
          </FormGroup>
        </ModalBody>
        <ModalFooter style={{ borderTop: `1px solid ${C.borderLight}`, gap: 8 }}>
          <Button style={primaryBtn} onClick={handleSave}>{editingSettings ? "Update" : "Create"}</Button>
          <Button style={secondaryBtn} onClick={() => setModalOpen(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalHeader toggle={() => setDeleteModal(false)} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700, color: C.text }}>Confirm Delete</span>
        </ModalHeader>
        <ModalBody style={{ padding: 24, fontFamily: "'Inter', sans-serif" }}>
          Are you sure you want to delete the buzzer integration for provider{" "}
          <strong>{deleteTarget?.provider}</strong> (ID: {deleteTarget?.id})?
        </ModalBody>
        <ModalFooter style={{ borderTop: `1px solid ${C.borderLight}`, gap: 8 }}>
          <Button style={{ background: "#dc3545", border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13, color: C.white }} onClick={handleDeleteConfirm}>Delete</Button>
          <Button style={secondaryBtn} onClick={() => setDeleteModal(false)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default BuzzerIntegration;
